import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { DocumentationViewer } from './DocumentationViewer';


/**
 * P_PLATFORM_DOCS_1.0 — the standard "? Help" icon used across the
 * platform. Renders a small round button; clicking it opens the
 * reusable `DocumentationViewer` on the doc identified by `docId`.
 *
 * Usage:
 *     <HelpButton docId="tapProtection" />
 *
 * Optional props:
 *     size       — "sm" (default) or "md"
 *     className  — additional Tailwind classes
 *     label      — visible label alongside the icon (useful in headers)
 */
export const HelpButton = ({
    docId,
    size = 'sm',
    className = '',
    label = null,
    ...props
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const iconSize = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
    const btnSize = size === 'md' ? 'h-8 w-8' : 'h-6 w-6';

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={t('docs.helpButton.aria', { defaultValue: 'Open in-app help' })}
                title={t('docs.helpButton.title', { defaultValue: 'Open the administrator guide' })}
                className={`
                    inline-flex items-center gap-1 rounded-full
                    text-slate-500 hover:text-emerald-700 hover:bg-emerald-50
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500
                    transition-colors
                    ${label ? 'px-2 py-1 text-xs font-medium' : btnSize + ' justify-center'}
                    ${className}
                `}
                data-testid={`help-btn-${docId}`}
                {...props}
            >
                <HelpCircle className={iconSize} />
                {label ? <span>{label}</span> : null}
            </button>
            <DocumentationViewer
                open={open}
                onOpenChange={setOpen}
                docId={docId}
            />
        </>
    );
};
